import type { JSX } from '@builder.io/qwik';
import { component$, useContext } from '@builder.io/qwik';
import { cva } from 'class-variance-authority';
import {
  getCSSColorBg,
  getCSSColorHoverBg,
  getCSSColorHoverText,
  getCSSColorText,
  getCSSSectionHeader,
  getCSSSizeLgText,
  getCSSSizeMdText,
  getCSSSizeSmText,
} from '~/utilities/getCSS';
import { AppDataContext } from '~/contexts/AppDataContext';
import { getFontSizeObject } from '~/utilities/getFontSizeObject';
import type { RelatedPagesInterface } from '~/types/AppData/PageContent/RelatedPagesInterface';
import { getSectionHeaderObject } from '~/utilities/getSectionHeaderObject';
import { ArticleContext } from '~/contexts/ArticleContext';

const sectionCSS = cva('related-topics mt-10 border-solid py-2.5', {
  variants: {
    ...getCSSSectionHeader(),
  },
});

const headingCSS = cva('border-solid', {
  variants: {
    color: getCSSColorText(),
    sizeSm: getCSSSizeSmText(),
    sizeMd: getCSSSizeMdText(),
    sizeLg: getCSSSizeLgText(),
  },
});

const linkCSS = cva(
  'topic-link inline-flex px-1.5 py-2 min-w-[60px] justify-center',
  {
    variants: {
      color: getCSSColorText(),
      colorHover: getCSSColorHoverText(),
      background: getCSSColorBg(),
      backgroundHover: getCSSColorHoverBg(),
      sizeSm: getCSSSizeSmText(),
      sizeMd: getCSSSizeMdText(),
      sizeLg: getCSSSizeLgText(),
    },
  }
);

const createRelatedTopicsData = (data: RelatedPagesInterface | null) => {
  const relatedTopicsData = {
    title: data?.title?.value || '',
    blockPosition: data?.blockPosition?.value || null,
    pages: data?.pages || [],
  };

  if (
    relatedTopicsData.title &&
    relatedTopicsData.pages.length > 2 &&
    (relatedTopicsData.blockPosition || relatedTopicsData.blockPosition === 0)
  ) {
    return relatedTopicsData;
  }
  return null;
};

export default component$((): JSX.Element => {
  const {
    themeComponents: { relatedTopics, sectionHeader },
    payloadToSSR: { pageContent },
  } = useContext(AppDataContext);

  const article = useContext(ArticleContext);

  const related = createRelatedTopicsData(pageContent.relatedPages);

  if (!related || article?.finiteScrollNumber !== 0) return <></>;

  return (
    <aside
      class={sectionCSS({
        ...getSectionHeaderObject(sectionHeader),
      })}
    >
      <h2
        class={headingCSS({
          color: relatedTopics.titleColor.value,
          ...getFontSizeObject(relatedTopics, 'titleFontSize'),
        })}
      >
        {pageContent.relatedPages?.title?.value || 'Related Topics'}
      </h2>
      <ul class='topics-list flex flex-wrap gap-y-4 gap-x-2.5 my-[18px] list-none ml-0'>
        {related.pages.map((topic, index) => {
          if (!topic.link || !topic.name) {
            return null;
          }
          return (
            <li key={index} class='topic'>
              <a
                href={topic.link}
                class={linkCSS({
                  color: relatedTopics.linkTextColorNormal.value,
                  colorHover: relatedTopics.linkTextColorHover.value,
                  background: relatedTopics.linkBackgroundColorNormal.value,
                  backgroundHover: relatedTopics.linkBackgroundColorHover.value,
                  ...getFontSizeObject(relatedTopics, 'linkFontSize'),
                })}
              >
                {topic.name}
              </a>
            </li>
          );
        })}
      </ul>
    </aside>
  );
});
